
import React, { useState, useMemo } from 'react';
import { useStore } from '../../context/StoreContext';
import { useToast } from '../../context/ToastContext';
import { 
  Plus, X, Trash2, Save, Layers, Search, Eye, EyeOff, Boxes, Image as ImageIcon, AlertCircle, Loader2, Sticker, Star, Flame, Zap, Heart, Trophy, Target, Gift
} from 'lucide-react';
import { Product, Category } from '../../types';

// Mock de stickers (Simulando 1400 opçoes via categorias)
const STICKER_PACKS = [
    { name: '🔥 Promo', icon: '🔥' },
    { name: '⭐ Top', icon: '⭐' },
    { name: '🆕 New', icon: '🆕' },
    { name: '🍔 Burg', icon: '🍔' },
    { name: '🍕 Pizz', icon: '🍕' },
    { name: '🍣 Sush', icon: '🍣' },
    { name: '🥤 Drink', icon: '🥤' },
    { name: '🧁 Sweet', icon: '🧁' },
    { name: '🥦 Veg', icon: '🥦' },
    { name: '🌶️ Hot', icon: '🌶️' },
    { name: '🧊 Cold', icon: '🧊' },
    { name: '👑 King', icon: '👑' },
    { name: '⚡ Fast', icon: '⚡' },
    { name: '🎁 Gift', icon: '🎁' },
    { name: '💎 Lux', icon: '💎' },
    { name: '🥇 #1', icon: '🥇' },
];

const MenuManager: React.FC = () => {
  const { products, categories, addCategory, deleteCategory, addItem, updateProduct, deleteProduct, updateCategory } = useStore();
  const { addToast } = useToast();
  
  const [activeTab, setActiveTab] = useState<'products' | 'categories'>('products');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const filteredProducts = useMemo(() => {
    return products.filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      categories.find(c => c.id === p.categoryId)?.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [products, searchTerm, categories]);

  const handleAddProduct = async () => {
    if (categories.length === 0) {
      addToast('Crie uma categoria primeiro.', 'error');
      setActiveTab('categories');
      return;
    }
    
    setIsCreating(true);
    try {
      const newItem = await addItem({ 
        name: 'NOVO ITEM BOLD', 
        price: 29.90, 
        description: 'Descrição vibrante do seu produto...', 
        categoryId: categories[0].id 
      });

      if (newItem) {
        setEditingProduct(newItem);
        addToast('Template criado!', 'success');
      }
    } catch (err) {
      addToast('Erro ao criar.', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveProduct = async () => {
    if (editingProduct) {
      try {
        await updateProduct(editingProduct);
        setEditingProduct(null);
        addToast('Publicado com sucesso!', 'success');
      } catch (err) {
        addToast('Erro ao salvar.', 'error');
      }
    }
  };

  const handleSaveCategory = async () => {
      if (editingCategory) {
          await updateCategory(editingCategory);
          setEditingCategory(null);
          addToast('Categoria atualizada!', 'success');
      }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-12 pb-24 animate-fade-in text-black">
      <header className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 border-b-4 border-black pb-8">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
             <div className="w-14 h-14 bg-brand-rose border-4 border-black flex items-center justify-center shadow-bold-sm rotate-3">
                <Boxes className="w-7 h-7 text-white" />
             </div>
             <h1 className="text-6xl md:text-7xl font-black text-black tracking-tighter uppercase italic leading-[0.75]">
               VIVA O <span className="text-brand-rose">MENU.</span>
             </h1>
          </div>
          
          <nav className="flex gap-3">
            <button 
              onClick={() => setActiveTab('products')} 
              className={`px-6 py-2.5 border-4 border-black font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'products' ? 'bg-black text-white shadow-bold-sm' : 'bg-white hover:bg-gray-100'}`}
            >
              Itens ({products.length})
            </button>
            <button 
              onClick={() => setActiveTab('categories')} 
              className={`px-6 py-2.5 border-4 border-black font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'categories' ? 'bg-black text-white shadow-bold-sm' : 'bg-white hover:bg-gray-100'}`}
            >
              Categorias ({categories.length})
            </button>
          </nav>
        </div>

        <div className="flex items-center gap-3 w-full lg:w-auto">
           <div className="relative flex-1 lg:w-[300px]">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-black" />
              <input 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="BUSCAR..."
                className="w-full bg-white border-4 border-black py-4 pl-12 pr-6 text-xl font-black italic uppercase outline-none focus:bg-brand-yellow transition-all shadow-bold-sm"
              />
           </div>
           <button 
             onClick={activeTab === 'products' ? handleAddProduct : () => {}} 
             disabled={isCreating}
             className="bg-brand-green text-black px-8 py-4 border-4 border-black font-black text-lg uppercase italic tracking-tighter flex items-center gap-4 hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-bold transition-all shadow-bold-sm active:translate-x-1 active:translate-y-1 active:shadow-none"
           >
             {isCreating ? <Loader2 className="w-6 h-6 animate-spin" /> : <Plus className="w-7 h-7" />}
             NOVO
           </button>
        </div>
      </header>

      {activeTab === 'categories' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div className="bg-white border-4 border-black border-dashed p-6 flex flex-col items-center justify-center gap-6 group hover:border-brand-rose transition-all min-h-[220px] shadow-bold-sm">
             <input 
               value={newCatName} 
               onChange={e => setNewCatName(e.target.value.toUpperCase())} 
               placeholder="NOME..." 
               className="bg-transparent text-center text-2xl font-black border-b-4 border-black pb-2 outline-none w-full italic" 
             />
             <button 
               onClick={() => { if(newCatName) { addCategory(newCatName); setNewCatName(''); addToast('Categoria criada!', 'success'); }}} 
               className="bg-black text-white px-6 py-3 border-2 border-black font-black uppercase text-[10px] tracking-widest hover:bg-brand-rose transition-all shadow-bold-sm"
             >
               REGISTRAR
             </button>
          </div>
          {categories.map(cat => (
            <div key={cat.id} className="bg-white border-4 border-black p-6 flex items-center justify-between group hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-bold transition-all shadow-bold-sm neo-pressable">
              <div className="flex items-center gap-4">
                 <div className="w-12 h-12 bg-brand-yellow border-2 border-black shadow-bold-sm flex items-center justify-center -rotate-6">
                    {cat.sticker ? <span className="text-2xl">{cat.sticker}</span> : <Layers className="w-6 h-6" />}
                 </div>
                 <div>
                    <h4 className="font-black text-2xl italic uppercase leading-none">{cat.name}</h4>
                    <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mt-1.5">
                       {products.filter(p => p.categoryId === cat.id).length} ITENS
                    </p>
                 </div>
              </div>
              <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setEditingCategory(cat)} className="w-10 h-10 bg-white border-2 border-black flex items-center justify-center text-black hover:bg-brand-blue hover:text-white transition-all shadow-bold-sm">
                    <Sticker className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => { if(confirm('Excluir categoria?')) deleteCategory(cat.id); }} 
                  className="w-10 h-10 bg-white border-2 border-black flex items-center justify-center text-black hover:bg-brand-rose hover:text-white transition-all shadow-bold-sm"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'products' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 pb-24">
          {filteredProducts.map(p => (
            <div key={p.id} className="bg-white border-4 border-black p-5 group hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-bold transition-all flex flex-col h-full shadow-bold-sm relative neo-pressable">
              <div className="aspect-[4/3] rounded-none overflow-hidden mb-5 border-2 border-black bg-black relative">
                <img src={p.imageUrl} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-90" alt={p.name} />
                
                {p.sticker && (
                    <div className="absolute -top-2 -left-2 bg-brand-yellow border-2 border-black px-2 py-1 shadow-bold-sm rotate-[-12deg] z-20 text-xl">
                        {p.sticker}
                    </div>
                )}

                <div className="absolute top-2 right-2 flex gap-1">
                   <div className={`px-2 py-0.5 border-2 border-black text-[8px] font-black uppercase tracking-widest shadow-bold-sm ${p.active ? 'bg-brand-green text-black' : 'bg-brand-rose text-white'}`}>
                      {p.active ? 'ON' : 'OFF'}
                   </div>
                </div>
              </div>
              
              <div className="space-y-2 mb-6 flex-1">
                <h3 className="text-2xl font-black italic uppercase leading-[0.8] mb-1 truncate">{p.name}</h3>
                <div className="bg-brand-yellow border-2 border-black px-2 py-1 w-fit shadow-bold-sm rotate-1">
                    <span className="font-black text-lg italic">R$ {p.price.toFixed(2)}</span>
                </div>
                <p className="text-gray-500 text-[10px] font-bold leading-tight line-clamp-2">
                   {p.description || 'SEM DESCRIÇÃO.'}
                </p>
              </div>

              <div className="flex gap-2">
                 <button 
                   onClick={() => setEditingProduct(p)} 
                   className="flex-1 bg-black text-white border-2 border-black py-3 font-black uppercase text-[9px] tracking-widest hover:bg-brand-blue transition-all shadow-bold-sm active:translate-x-1 active:translate-y-1"
                 >
                   EDITAR
                 </button>
                 <button 
                   onClick={() => { if(confirm('Excluir produto?')) deleteProduct(p.id); }} 
                   className="w-10 h-10 bg-brand-rose border-2 border-black text-white flex items-center justify-center hover:bg-black transition-all shadow-bold-sm active:translate-x-1 active:translate-y-1"
                 >
                   <Trash2 className="w-5 h-5" />
                 </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingProduct && (
        <div className="fixed inset-0 bg-brand-yellow/80 backdrop-blur-md z-[200] flex items-center justify-center p-4 animate-fade-in">
           <div className="bg-white border-[8px] border-black w-full max-w-4xl p-8 lg:p-12 relative animate-scale-in max-h-[90vh] overflow-y-auto no-scrollbar shadow-[16px_16px_0px_0px_rgba(0,0,0,1)]">
              <button 
                onClick={() => setEditingProduct(null)} 
                className="absolute top-4 right-4 w-12 h-12 bg-brand-rose border-4 border-black text-white flex items-center justify-center hover:rotate-90 transition-transform shadow-bold-sm"
              >
                <X className="w-7 h-7" />
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                 <div className="space-y-6">
                    <div className="aspect-square bg-black border-4 border-black relative group shadow-bold rotate-[-1deg]">
                       <img src={editingProduct.imageUrl} className="w-full h-full object-cover opacity-90" />
                       <div className="absolute inset-0 bg-brand-blue/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer">
                          <ImageIcon className="w-10 h-10 text-white mb-2" />
                          <span className="text-[10px] font-black uppercase tracking-widest text-white">Trocar Imagem</span>
                       </div>
                    </div>

                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-widest block ml-1 flex items-center gap-2">
                           <Sticker className="w-4 h-4 text-brand-rose" /> Visual Sticker (Badge)
                        </label>
                        <div className="grid grid-cols-8 gap-2 bg-gray-100 p-4 border-2 border-black shadow-bold-sm">
                            <button onClick={() => setEditingProduct({...editingProduct, sticker: undefined})} className={`p-2 border-2 border-black flex items-center justify-center text-[10px] font-black ${!editingProduct.sticker ? 'bg-black text-white' : 'bg-white'}`}>X</button>
                            {STICKER_PACKS.map(s => (
                                <button 
                                    key={s.name} 
                                    onClick={() => setEditingProduct({...editingProduct, sticker: s.icon})}
                                    className={`p-2 border-2 border-black text-xl hover:bg-brand-yellow transition-all ${editingProduct.sticker === s.icon ? 'bg-brand-yellow scale-110' : 'bg-white'}`}
                                >
                                    {s.icon}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="bg-brand-offwhite border-2 border-black p-5 shadow-bold-sm space-y-4">
                       <h5 className="text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-brand-rose" /> Disponibilidade
                       </h5>
                       <div className="flex gap-2">
                          <button 
                            onClick={() => setEditingProduct({...editingProduct, active: !editingProduct.active})}
                            className={`flex-1 py-3 border-2 border-black font-black uppercase text-[9px] tracking-widest transition-all ${editingProduct.active ? 'bg-brand-green shadow-bold-sm' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {editingProduct.active ? 'ATIVO' : 'OCULTO'}
                          </button>
                          <button 
                            onClick={() => setEditingProduct({...editingProduct, inStock: !editingProduct.inStock})}
                            className={`flex-1 py-3 border-2 border-black font-black uppercase text-[9px] tracking-widest transition-all ${editingProduct.inStock ? 'bg-brand-blue text-white shadow-bold-sm' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {editingProduct.inStock ? 'ESTOQUE' : 'FALTA'}
                          </button>
                       </div>
                    </div>
                 </div>

                 <div className="space-y-6">
                    <div className="space-y-2">
                       <h2 className="text-4xl md:text-5xl font-black italic uppercase leading-[0.8] mb-2">EDITAR <span className="text-brand-rose">ITEM.</span></h2>
                       <p className="text-[9px] font-black uppercase tracking-widest bg-black text-white px-2 py-0.5 w-fit border-2 border-black">ID: {editingProduct.id.slice(0,8)}</p>
                    </div>

                    <div className="space-y-6">
                       <div className="space-y-1.5">
                          <label className="text-[10px] font-black uppercase tracking-widest block ml-1">Título</label>
                          <input 
                            value={editingProduct.name} 
                            onChange={e => setEditingProduct({...editingProduct, name: e.target.value.toUpperCase()})} 
                            className="w-full bg-gray-50 border-4 border-black p-4 text-2xl font-black italic outline-none focus:bg-brand-yellow shadow-bold-sm" 
                          />
                       </div>

                       <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                             <label className="text-[10px] font-black uppercase tracking-widest block ml-1">Preço</label>
                             <input 
                               type="number"
                               value={editingProduct.price} 
                               onChange={e => setEditingProduct({...editingProduct, price: parseFloat(e.target.value) || 0})} 
                               className="w-full bg-gray-50 border-4 border-black p-4 text-2xl font-black outline-none focus:bg-brand-yellow shadow-bold-sm" 
                             />
                          </div>
                          <div className="space-y-1.5">
                             <label className="text-[10px] font-black uppercase tracking-widest block ml-1">Cat</label>
                             <select 
                               value={editingProduct.categoryId} 
                               onChange={e => setEditingProduct({...editingProduct, categoryId: e.target.value})} 
                               className="w-full bg-gray-50 border-4 border-black p-4 text-base font-black uppercase italic outline-none focus:bg-brand-yellow shadow-bold-sm appearance-none"
                             >
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                             </select>
                          </div>
                       </div>

                       <div className="space-y-1.5">
                          <label className="text-[10px] font-black uppercase tracking-widest block ml-1">Descrição</label>
                          <textarea 
                            value={editingProduct.description} 
                            onChange={e => setEditingProduct({...editingProduct, description: e.target.value})} 
                            className="w-full bg-gray-50 border-4 border-black p-4 text-black h-32 resize-none outline-none focus:bg-brand-yellow text-sm font-bold leading-tight shadow-bold-sm" 
                            placeholder="DESCREVA..."
                          />
                       </div>
                    </div>

                    <button 
                      onClick={handleSaveProduct} 
                      className="w-full bg-black text-white py-6 border-4 border-black font-black text-xl uppercase italic tracking-tighter hover:bg-brand-rose transition-all shadow-bold active:translate-x-1 active:translate-y-1 active:shadow-none"
                    >
                       SALVAR <Save className="w-6 h-6 inline-block ml-2" />
                    </button>
                 </div>
              </div>
           </div>
        </div>
      )}

      {editingCategory && (
        <div className="fixed inset-0 bg-brand-rose/80 backdrop-blur-md z-[200] flex items-center justify-center p-4 animate-fade-in">
           <div className="bg-white border-[8px] border-black w-full max-w-lg p-10 relative animate-scale-in shadow-bold-xl">
              <button onClick={() => setEditingCategory(null)} className="absolute top-4 right-4"><X className="w-8 h-8" /></button>
              <h3 className="text-3xl font-black uppercase italic italic mb-8">Personalizar Categoria</h3>
              
              <div className="space-y-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase">Nome</label>
                    <input 
                        value={editingCategory.name}
                        onChange={e => setEditingCategory({...editingCategory, name: e.target.value.toUpperCase()})}
                        className="w-full bg-gray-100 border-4 border-black p-4 text-2xl font-black italic outline-none"
                    />
                  </div>

                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase flex items-center gap-2"><Sticker className="w-4 h-4" /> Sticker da Categoria</label>
                    <div className="grid grid-cols-6 gap-2">
                        {STICKER_PACKS.map(s => (
                            <button 
                                key={s.name}
                                onClick={() => setEditingCategory({...editingCategory, sticker: s.icon})}
                                className={`p-3 border-2 border-black text-2xl ${editingCategory.sticker === s.icon ? 'bg-brand-yellow shadow-bold-sm' : 'bg-white'}`}
                            >
                                {s.icon}
                            </button>
                        ))}
                    </div>
                  </div>

                  <button onClick={handleSaveCategory} className="w-full bg-black text-white py-6 border-4 border-black font-black text-xl uppercase italic shadow-bold">SALVAR CATEGORIA</button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default MenuManager;
